import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import BillingService, {
  BillingProduct,
  CompanyProduct,
  InvoiceIssuerCompany,
  RenewalCase,
  SaveRenewalCase,
} from '../../../services/BillingService';
import SuperAdminService, {
  Company,
  FiscalDevice,
  SuperAdminReminder,
} from '../../../services/SuperAdminService';

interface Props {
  open: boolean;
  editing?: RenewalCase | null;
  /** Bir hatırlatmadan ön-doldurma (opsiyonel). */
  prefill?: SuperAdminReminder | null;
  onClose: () => void;
  onSaved: () => void;
}

type LineForm = {
  billingProductId?: number | null;
  fiscalDeviceId?: number | null;
  /** device: yazarkasa, product: şirket ürünü, manual: elle eklenen. */
  kind: 'device' | 'product' | 'manual';
  /** Marka ve model. Yazarkasada seri ve şubeden ayrı durur. */
  deviceName: string;
  serialNumber: string;
  branchName: string;
  name: string;
  /** Katalog ya da elle girilen başlangıç tutarı. */
  listPrice: number;
  /** Liste fiyatına eklenen ya da düşülen tutar. Artı artırır, eksi azaltır. */
  priceAdjustment: number;
  /** Tahsil edilen birim fiyat = listPrice + priceAdjustment. */
  unitPrice: number;
  quantity: number;
  vatRate: number;
};

const roundMoney = (n: number) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.round(x * 100) / 100;
};

const makeLine = (partial: Partial<LineForm> = {}): LineForm => {
  const priceAdjustment = roundMoney(partial.priceAdjustment ?? 0);
  const listPrice = roundMoney(
    partial.listPrice ?? roundMoney((partial.unitPrice ?? 0) - priceAdjustment)
  );
  return {
    billingProductId: partial.billingProductId,
    fiscalDeviceId: partial.fiscalDeviceId,
    kind: partial.kind ?? 'manual',
    deviceName: partial.deviceName ?? '',
    serialNumber: partial.serialNumber ?? '',
    branchName: partial.branchName ?? '',
    name: partial.name ?? '',
    listPrice,
    priceAdjustment,
    unitPrice: roundMoney(listPrice + priceAdjustment),
    quantity: partial.quantity ?? 1,
    vatRate: partial.vatRate ?? 20,
  };
};

const emptyLine: LineForm = makeLine();

const money = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type DatedGroup = {
  date: string;
  devices: FiscalDevice[];
  products: CompanyProduct[];
};

/** Şirketteki yazarkasa ve ürünleri son tarihe göre ayırır. Tarihi olmayanlar gruba girmez. */
const buildDateGroups = (deviceList: FiscalDevice[], productList: CompanyProduct[]): DatedGroup[] => {
  const map = new Map<string, DatedGroup>();
  const bucket = (date: string): DatedGroup => {
    let group = map.get(date);
    if (!group) {
      group = { date, devices: [], products: [] };
      map.set(date, group);
    }
    return group;
  };
  for (const device of deviceList) {
    const date = isoDate(device.expiryDate);
    if (date) bucket(date).devices.push(device);
  }
  for (const product of productList) {
    if (!product.isActive) continue;
    const date = isoDate(product.contractEndDate);
    if (date) bucket(date).products.push(product);
  }
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
};

const pickDefaultDate = (groups: DatedGroup[], preferred: string) => {
  if (preferred && groups.some((group) => group.date === preferred)) return preferred;
  if (groups.length === 0) return preferred;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const upcoming = groups.find((group) => group.date >= today);
  return upcoming?.date || groups[groups.length - 1].date;
};

const groupSummary = (group: DatedGroup) => {
  const parts: string[] = [];
  if (group.devices.length) parts.push(`${group.devices.length} yazarkasa`);
  if (group.products.length) parts.push(`${group.products.length} ürün`);
  return parts.join(' · ');
};

const trDate = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');

const reminderDaysLabel = (days: number) =>
  days < 0 ? `${Math.abs(days)} gün geçti` : days === 0 ? 'Bugün doluyor' : `${days} gün kaldı`;

const reminderOptionLabel = (r: SuperAdminReminder) => {
  const what = [r.subtitle, r.detail].filter(Boolean).join(' · ');
  const when = [trDate(isoDate(r.expiryDate)), reminderDaysLabel(r.daysRemaining)].filter(Boolean).join(' · ');
  return [r.title, what, when].filter(Boolean).join(' — ');
};

const shortDevice = (device: FiscalDevice) => {
  const who = [device.brand, device.model].filter(Boolean).join(' ');
  return [who || 'Yazarkasa', device.serialNumber].filter(Boolean).join(' · ');
};

const InfoLine = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'baseline' }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const linesFromCompanyProducts = (items: CompanyProduct[]): LineForm[] =>
  items
    .filter((p) => p.isActive)
    .map((p) =>
      makeLine({
        kind: 'product',
        deviceName: p.name,
        name: p.name,
        listPrice: p.price,
        quantity: 1,
        vatRate: p.vatRate,
      })
    );

const sourceIdFromKey = (key: string): string | undefined => {
  // "fiscal:123" -> "123", "contract:xyz" -> "xyz"
  const idx = key.indexOf(':');
  return idx >= 0 ? key.slice(idx + 1) : undefined;
};

const isoDate = (v?: string | null) => (v ? v.slice(0, 10) : '');

const normalizeDevice = (raw: any): FiscalDevice => ({
  ...raw,
  id: raw.id ?? raw.Id,
  companyId: raw.companyId ?? raw.CompanyId ?? '',
  email: raw.email ?? raw.Email ?? null,
  phone: raw.phone ?? raw.Phone ?? null,
  branch: raw.branch ?? raw.Branch ?? null,
  branchId: raw.branchId ?? raw.BranchId ?? null,
  brand: raw.brand ?? raw.Brand ?? null,
  model: raw.model ?? raw.Model ?? null,
  serialNumber: raw.serialNumber ?? raw.SerialNumber ?? null,
  expiryDate: raw.expiryDate ?? raw.ExpiryDate ?? null,
});

const CaseFormDialog: React.FC<Props> = ({ open, editing, prefill, onClose, onSaved }) => {
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [products, setProducts] = React.useState<BillingProduct[]>([]);
  const [issuers, setIssuers] = React.useState<InvoiceIssuerCompany[]>([]);

  const [companyId, setCompanyId] = React.useState<string>('');
  const [sourceType, setSourceType] = React.useState<'FiscalDevice' | 'Contract' | 'Manual'>('Manual');
  const [sourceId, setSourceId] = React.useState<string>('');
  const [expiryDate, setExpiryDate] = React.useState<string>('');
  const [issuerCompanyId, setIssuerCompanyId] = React.useState<number | ''>('');
  const [announcementScope, setAnnouncementScope] = React.useState<'Company' | 'Branches'>('Company');
  const [announcementLeadDays, setAnnouncementLeadDays] = React.useState<number>(30);
  const [defaultLeadDays, setDefaultLeadDays] = React.useState<number>(30);
  const [branchId, setBranchId] = React.useState<string>('');
  const [announcementTitle, setAnnouncementTitle] = React.useState<string>('');
  const [announcementMessage, setAnnouncementMessage] = React.useState<string>('');
  const [notifyEmail, setNotifyEmail] = React.useState<string>('');
  const [notifyPhone, setNotifyPhone] = React.useState<string>('');
  const [notes, setNotes] = React.useState<string>('');
  const [lines, setLines] = React.useState<LineForm[]>([{ ...emptyLine }]);
  const [adjustDraft, setAdjustDraft] = React.useState<Record<number, string>>({});

  const [branches, setBranches] = React.useState<
    {
      branchId: string;
      name: string;
      email?: string | null;
      phone?: string | null;
      firmEmail?: string | null;
      firmPhone?: string | null;
      isFranchise?: boolean;
      firmName?: string | null;
    }[]
  >([]);
  const [devices, setDevices] = React.useState<FiscalDevice[]>([]);
  const [companyProducts, setCompanyProducts] = React.useState<CompanyProduct[]>([]);
  const [reminders, setReminders] = React.useState<SuperAdminReminder[]>([]);
  const [reminderKey, setReminderKey] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loadingRefs, setLoadingRefs] = React.useState(false);
  const [loadingCompany, setLoadingCompany] = React.useState(false);

  const productPrice = (p: BillingProduct) => {
    const n = Number(p.defaultPrice);
    return Number.isFinite(n) ? n : 0;
  };

  const catalogForDevice = React.useCallback(
    (deviceName: string): BillingProduct | undefined => {
      const fiscal = products.filter((p) => p.isActive && p.itemType === 'FiscalDevice');
      const fold = (s: string) => s.toLocaleLowerCase('tr').replace(/[^a-z0-9çğıöşü]/g, '');
      const key = fold(deviceName);
      const tokens = deviceName
        .toLocaleLowerCase('tr')
        .split(/[^a-z0-9çğıöşü]+/i)
        .map(fold)
        .filter((t) => t.length >= 3);
      let best: BillingProduct | undefined;
      let bestScore = 0;
      for (const p of fiscal) {
        const n = fold(p.name);
        if (!n) continue;
        const score =
          key && (n === key || key.includes(n) || n.includes(key))
            ? 10
            : tokens.filter((t) => n.includes(t)).length;
        if (score > bestScore) {
          best = p;
          bestScore = score;
        }
      }
      if (best && bestScore > 0) return best;
      return fiscal.find((p) => /yazarkasa gmü/i.test(p.name)) ?? fiscal[0];
    },
    [products]
  );

  const lineFromCatalog = React.useCallback(
    (itemType: BillingProduct['itemType'], fallbackName: string, deviceName?: string): LineForm => {
      const catalog =
        itemType === 'FiscalDevice' && deviceName
          ? catalogForDevice(deviceName)
          : products.find((p) => p.itemType === itemType && p.isActive) ??
            products.find((p) => p.itemType === itemType);
      if (catalog) {
        return makeLine({
          billingProductId: catalog.id,
          name: fallbackName ? `${catalog.name} — ${fallbackName}` : catalog.name,
          listPrice: productPrice(catalog),
          quantity: 1,
          vatRate: catalog.vatRate,
        });
      }
      return makeLine({ name: fallbackName || '' });
    },
    [catalogForDevice, products]
  );

  const lineForDevice = React.useCallback(
    (d: FiscalDevice, existing?: LineForm, branchNameOverride?: string): LineForm => {
      const deviceName = [d.brand, d.model].filter(Boolean).join(' ').trim() || 'Yazarkasa';
      const serialNumber = (d.serialNumber || '').trim();
      const branchName = (branchNameOverride || d.branch || '').trim();
      const catalog = catalogForDevice(deviceName);
      const catalogPrice = catalog ? productPrice(catalog) : 0;
      if (existing && existing.fiscalDeviceId === d.id) {
        const listPrice = existing.listPrice > 0 ? existing.listPrice : catalogPrice;
        return makeLine({
          ...existing,
          kind: 'device',
          deviceName: existing.deviceName || deviceName,
          serialNumber: existing.serialNumber || serialNumber,
          branchName: existing.branchName || branchName,
          billingProductId: existing.billingProductId && existing.listPrice > 0 ? existing.billingProductId : catalog?.id ?? existing.billingProductId,
          listPrice,
          priceAdjustment: existing.priceAdjustment,
          vatRate: existing.vatRate || catalog?.vatRate || 20,
        });
      }
      const base = lineFromCatalog('FiscalDevice', deviceName, deviceName);
      const serviceName = base.billingProductId ? base.name.split(' — ')[0].trim() : '';
      const name = [serviceName, deviceName !== serviceName ? deviceName : '', serialNumber ? `SN ${serialNumber}` : '', branchName]
        .filter(Boolean)
        .join(' · ');
      return {
        ...base,
        name,
        fiscalDeviceId: d.id,
        kind: 'device',
        deviceName,
        serialNumber,
        branchName,
      };
    },
    [catalogForDevice, lineFromCatalog]
  );

  const applyDate = React.useCallback(
    (
      date: string,
      deviceList: FiscalDevice[],
      productList: CompanyProduct[],
      branchOpts: typeof branches,
      companyMail: string
    ) => {
      const groupDevices = deviceList.filter((d) => isoDate(d.expiryDate) === date);
      const groupProducts = productList.filter((p) => p.isActive && isoDate(p.contractEndDate) === date);
      setExpiryDate(date);

      const branchIds = [
        ...new Set(groupDevices.map((d) => d.branchId).filter((id): id is string => !!id)),
      ];
      const deviceMail = groupDevices.find((d) => d.email)?.email || '';
      let mail = deviceMail || companyMail;
      if (branchIds.length === 1) {
        const branch = branchOpts.find((b) => b.branchId === branchIds[0]);
        setBranchId(branchIds[0]);
        setAnnouncementScope('Branches');
        const branchMail = (branch?.email || branch?.firmEmail || '').trim();
        if (branchMail) mail = branchMail;
      } else if (branchIds.length === 0 && branchOpts.length === 1) {
        const branch = branchOpts[0];
        setBranchId(branch.branchId);
        setAnnouncementScope('Branches');
        const branchMail = (branch.email || branch.firmEmail || '').trim();
        if (branchMail) mail = branchMail;
      } else {
        setBranchId('');
        setAnnouncementScope('Company');
      }
      if (mail) setNotifyEmail(mail);

      if (groupDevices.length) {
        setSourceType('FiscalDevice');
        setSourceId(groupDevices.map((d) => d.id).join(','));
      } else if (groupProducts.length) {
        setSourceType('Manual');
        setSourceId('');
      } else if (prefill && (prefill.kind === 'CONTRACT' || prefill.kind === 'SUBSCRIPTION')) {
        setSourceType('Contract');
        setSourceId(companyId);
        setLines([lineFromCatalog('Contract', 'Panel sözleşme yenileme')]);
        return;
      } else {
        setSourceType('Manual');
        setSourceId('');
      }

      setLines((prev) => {
        const deviceLines = groupDevices.map((d) => {
          const branchName = (
            branchOpts.find((b) => b.branchId && b.branchId === d.branchId)?.name ||
            d.branch ||
            ''
          ).trim();
          return lineForDevice(
            d,
            prev.find((l) => l.fiscalDeviceId === d.id),
            branchName
          );
        });
        const productLines = linesFromCompanyProducts(groupProducts).filter(
          (l) => !deviceLines.some((d) => d.name.trim() === l.name.trim())
        );
        const merged = [...deviceLines, ...productLines];
        return merged.length ? merged : [{ ...emptyLine }];
      });
    },
    [companyId, lineForDevice, lineFromCatalog, prefill]
  );

  const applyDateRef = React.useRef(applyDate);
  applyDateRef.current = applyDate;
  const companiesRef = React.useRef(companies);
  companiesRef.current = companies;

  // İlk açılışta şirketler, şube/yazarkasa kaynakları, ürünler ve fatura şirketleri.
  React.useEffect(() => {
    if (!open) return;
    (async () => {
      setLoadingRefs(true);
      try {
        const svc = SuperAdminService.getInstance();
        const [c, p, i, r, s] = await Promise.all([
          svc.getAllCompanies(),
          BillingService.getProducts(false),
          BillingService.getIssuers(false),
          svc.getSuperAdminReminders(365).catch(() => [] as SuperAdminReminder[]),
          BillingService.getBillingSettings().catch(() => ({ defaultAnnouncementLeadDays: 30 })),
        ]);
        setCompanies(Array.isArray(c) ? c : []);
        setProducts(Array.isArray(p) ? p : []);
        setIssuers(Array.isArray(i) ? i : []);
        setReminders(Array.isArray(r) ? r : []);
        const def = s?.defaultAnnouncementLeadDays > 0 ? s.defaultAnnouncementLeadDays : 30;
        setDefaultLeadDays(def);
        // Yeni kayıt: global varsayılanı ön-doldur (düzenlemede editing değeri kullanılır).
        if (!editing) setAnnouncementLeadDays(def);
        if (!editing && !issuerCompanyId && Array.isArray(i) && i.length > 0) {
          setIssuerCompanyId(i[0].id);
        }
      } catch (e: any) {
        setError(e?.message || 'Referans veriler yüklenemedi.');
      } finally {
        setLoadingRefs(false);
      }
    })();
  }, [open]);

  // Form durumunu editing/prefill'e göre ilklendir.
  React.useEffect(() => {
    if (!open) return;
    if (editing) {
      setCompanyId(editing.companyId);
      setSourceType(editing.sourceType);
      setSourceId(editing.sourceId ?? '');
      setExpiryDate(editing.expiryDate ? editing.expiryDate.slice(0, 10) : '');
      setIssuerCompanyId(editing.issuerCompanyId ?? '');
      setAnnouncementScope(editing.announcementScope);
      setAnnouncementLeadDays(editing.announcementLeadDays && editing.announcementLeadDays > 0 ? editing.announcementLeadDays : defaultLeadDays);
      setBranchId(editing.branchId ?? '');
      setAnnouncementTitle(editing.announcementTitle ?? '');
      setAnnouncementMessage(editing.announcementMessage ?? '');
      setNotifyEmail(editing.notifyEmail ?? '');
      setNotifyPhone(editing.notifyPhone ?? '');
      setNotes(editing.notes ?? '');
      setLines(
        editing.lines.length
          ? editing.lines.map((l) => {
              const priceAdjustment = roundMoney(l.priceAdjustment ?? 0);
              return makeLine({
                billingProductId: l.billingProductId ?? null,
                name: l.name,
                listPrice: roundMoney((l.unitPrice || 0) - priceAdjustment),
                priceAdjustment,
                quantity: l.quantity,
                vatRate: l.vatRate,
              });
            })
          : [{ ...emptyLine }]
      );
    } else {
      setCompanyId(prefill?.companyId ?? '');
      setSourceType(
        prefill
          ? prefill.kind === 'FISCAL_DEVICE'
            ? 'FiscalDevice'
            : prefill.kind === 'PRODUCT' || prefill.kind === 'BILLING' || prefill.kind === 'DEMO'
            ? 'Manual'
            : 'Contract'
          : 'Manual'
      );
      setSourceId(
        prefill && prefill.kind !== 'PRODUCT' && prefill.kind !== 'BILLING' && prefill.kind !== 'DEMO'
          ? sourceIdFromKey(prefill.key) ?? ''
          : ''
      );
      setExpiryDate(prefill?.expiryDate ? prefill.expiryDate.slice(0, 10) : '');
      setIssuerCompanyId('');
      setAnnouncementScope(prefill?.kind === 'FISCAL_DEVICE' ? 'Branches' : 'Company');
      setAnnouncementLeadDays(defaultLeadDays);
      setBranchId('');
      setAnnouncementTitle('');
      setAnnouncementMessage('');
      setNotifyEmail('');
      setNotifyPhone('');
      setNotes('');
      setReminderKey('');
      setLines([{ ...emptyLine }]);
    }
    setError(null);
  }, [open, editing, prefill]);

  const expiryRef = React.useRef(expiryDate);
  expiryRef.current = expiryDate;
  const editingRef = React.useRef(editing);
  editingRef.current = editing;

  // Sözleşme kaydında katalog geç gelirse boş satırı doldur. Tarihli kalemleri ezmez.
  React.useEffect(() => {
    if (!open || editing || products.length === 0 || sourceType !== 'Contract') return;
    setLines((prev) => {
      const onlyEmpty = prev.length === 1 && !prev[0].name.trim();
      if (!onlyEmpty) return prev;
      return [lineFromCatalog('Contract', 'Panel sözleşme yenileme')];
    });
  }, [open, editing, products, sourceType, lineFromCatalog]);

  // Yazarkasa satırı katalogdan önce kurulduysa fiyatı sonradan doldur.
  React.useEffect(() => {
    if (!open || editing || products.length === 0) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (!line.fiscalDeviceId || line.listPrice > 0 || line.priceAdjustment) return line;
        const device = devices.find((d) => d.id === line.fiscalDeviceId);
        if (!device) return line;
        const fresh = lineForDevice(device);
        if (fresh.listPrice === line.listPrice && fresh.name === line.name) return line;
        changed = true;
        return { ...fresh, quantity: line.quantity };
      });
      return changed ? next : prev;
    });
  }, [open, editing, products, devices, lineForDevice]);

  // Şirket seçilince şubeler + yazarkasalar otomatik gelir; e-posta/şube boşsa doldurulur.
  React.useEffect(() => {
    if (!open || !companyId) {
      setBranches([]);
      setDevices([]);
      setCompanyProducts([]);
      setLoadingCompany(false);
      return;
    }
    let cancelled = false;
    setLoadingCompany(true);
    (async () => {
      const svc = SuperAdminService.getInstance();
      try {
        const [opts, rawDevices, compProducts] = await Promise.all([
          svc.getCompanyBranchOptions(companyId),
          // Yalnız merkez cihazları. Bayi yazarkasası tahsilata girmez;
          // hatırlatması Bayi Bildirimleri sekmesinden açılır.
          svc.getFiscalDevices(companyId, 'central').catch(() => [] as FiscalDevice[]),
          BillingService.getCompanyProducts(companyId).catch(() => [] as CompanyProduct[]),
        ]);
        if (cancelled) return;
        const companyProductList = Array.isArray(compProducts) ? compProducts : [];
        setCompanyProducts(companyProductList);
        const branchOpts = (opts || [])
          .filter((b) => b.branchId)
          .map((b) => ({
            branchId: b.branchId,
            name: b.name || b.branchId,
            email: b.email,
            phone: b.phone,
            firmEmail: b.firmEmail,
            firmPhone: b.firmPhone,
            isFranchise: b.isFranchise,
            firmName: b.firmName,
          }));
        setBranches(branchOpts);
        const list = (rawDevices || []).map(normalizeDevice);
        setDevices(list);

        if (editingRef.current) return;
        const groups = buildDateGroups(list, companyProductList);
        const chosen = pickDefaultDate(groups, isoDate(expiryRef.current));
        const companyMail = companiesRef.current.find((c) => c.id === companyId)?.admin?.email || '';
        if (chosen) {
          applyDateRef.current(chosen, list, companyProductList, branchOpts, companyMail);
        } else if (companyMail) {
          setNotifyEmail(companyMail);
        }
      } catch {
        if (!cancelled) {
          setBranches([]);
          setDevices([]);
        }
      } finally {
        if (!cancelled) setLoadingCompany(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId]);

  React.useEffect(() => {
    if (!editing && issuerCompanyId === '' && issuers.length > 0) {
      setIssuerCompanyId(issuers[0].id);
    }
  }, [issuers, editing, issuerCompanyId]);

  const phoneForEmail = React.useCallback((email: string) => {
    const key = email.trim().toLocaleLowerCase('tr');
    if (!key) return '';
    const company = companies.find((c) => (c.admin?.email || '').trim().toLocaleLowerCase('tr') === key);
    if (company?.admin?.phone?.trim()) return company.admin.phone.trim();
    for (const b of branches) {
      if ((b.email || '').trim().toLocaleLowerCase('tr') === key && b.phone?.trim()) return b.phone.trim();
      if ((b.firmEmail || '').trim().toLocaleLowerCase('tr') === key && b.firmPhone?.trim()) return b.firmPhone.trim();
    }
    const device = devices.find((d) => (d.email || '').trim().toLocaleLowerCase('tr') === key);
    return device?.phone?.trim() || '';
  }, [companies, branches, devices]);

  const phoneFilledFor = React.useRef('');
  React.useEffect(() => {
    if (!open) {
      phoneFilledFor.current = '';
      return;
    }
    const email = notifyEmail.trim();
    if (!email.includes('@')) return;
    const key = email.toLocaleLowerCase('tr');
    if (phoneFilledFor.current === key) return;
    const savedEmail = (editing?.notifyEmail || '').trim().toLocaleLowerCase('tr');
    if (editing?.notifyPhone?.trim() && key === savedEmail) {
      phoneFilledFor.current = key;
      return;
    }
    const local = phoneForEmail(email);
    if (local) {
      phoneFilledFor.current = key;
      setNotifyPhone(local);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const phone = await BillingService.phoneForEmail(email, companyId || undefined);
        if (cancelled) return;
        phoneFilledFor.current = key;
        if (phone) setNotifyPhone(phone);
      } catch {
        if (!cancelled) phoneFilledFor.current = '';
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, notifyEmail, companyId, editing, phoneForEmail]);

  const sortedCompanies = React.useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name, 'tr')),
    [companies]
  );
  const reminderOptions = React.useMemo(
    () => [...reminders].sort((a, b) => a.daysRemaining - b.daysRemaining || a.title.localeCompare(b.title, 'tr')),
    [reminders]
  );

  const dateGroups = React.useMemo(
    () => buildDateGroups(devices, companyProducts),
    [devices, companyProducts]
  );
  const activeGroup = dateGroups.find((g) => g.date === expiryDate) ?? null;

  const selectedBranch = branches.find((b) => b.branchId === branchId) ?? null;
  const franchiseLabel =
    selectedBranch && (selectedBranch.isFranchise || selectedBranch.firmName)
      ? selectedBranch.firmName || 'Franchise'
      : '';
  const sourceLabel =
    sourceType === 'FiscalDevice' ? 'Yazarkasa' : sourceType === 'Contract' ? 'Sözleşme' : 'Manuel';
  const scopeLabel = announcementScope === 'Branches' ? 'Şube' : 'Tüm şirket';
  const activeBranchNames = activeGroup
    ? [
        ...new Set(
          activeGroup.devices
            .map((d) => branches.find((b) => b.branchId === d.branchId)?.name || (d.branch || '').trim())
            .filter(Boolean)
        ),
      ]
    : [];
  const branchText = activeBranchNames.length
    ? activeBranchNames.join(', ')
    : selectedBranch?.name || 'Şirket geneli';

  const total = lines.reduce((sum, l) => sum + (l.unitPrice || 0) * (l.quantity || 0), 0);

  const applyProductToLine = (index: number, product: BillingProduct | null) => {
    setAdjustDraft((d) => {
      const next = { ...d };
      delete next[index];
      return next;
    });
    setLines((prev) =>
      prev.map((l, idx) =>
        idx === index
          ? product
            ? makeLine({
                ...l,
                billingProductId: product.id,
                name: product.name,
                listPrice: product.defaultPrice,
                priceAdjustment: 0,
                quantity: l.quantity || 1,
                vatRate: product.vatRate,
              })
            : { ...l, billingProductId: null }
          : l
      )
    );
  };

  const updateLine = (index: number, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((l, idx) => (idx === index ? { ...l, ...patch } : l)));
  };

  const updateLinePrice = (index: number, patch: Partial<Pick<LineForm, 'listPrice' | 'priceAdjustment'>>) => {
    setLines((prev) =>
      prev.map((l, idx) => (idx === index ? makeLine({ ...l, ...patch }) : l))
    );
  };

  const handleSave = async () => {
    if (!companyId) {
      setError('Şirket seçilmelidir.');
      return;
    }
    const validLines = lines.filter((l) => l.name.trim());
    if (validLines.length === 0) {
      setError('En az bir kalem eklenmelidir.');
      return;
    }
    if (validLines.some((l) => !(l.unitPrice > 0))) {
      setError('Her kalemin tutarı 0’dan büyük olmalıdır.');
      return;
    }
    if (announcementScope === 'Branches' && !branchId) {
      setError('Şube kapsamı için bir şube seçin.');
      return;
    }

    const payload: SaveRenewalCase = {
      id: editing?.id,
      companyId,
      sourceType,
      sourceId: sourceId || null,
      expiryDate: expiryDate || null,
      issuerCompanyId: issuerCompanyId === '' ? null : Number(issuerCompanyId),
      announcementScope,
      announcementLeadDays: announcementLeadDays > 0 ? announcementLeadDays : defaultLeadDays,
      branchId: announcementScope === 'Branches' ? branchId : null,
      announcementTitle: announcementTitle || null,
      announcementMessage: announcementMessage || null,
      notifyEmail: notifyEmail || null,
      notifyPhone: notifyPhone || null,
      emailSubject: null,
      emailHtml: null,
      notes: notes || null,
      lines: validLines.map((l) => {
        const priceAdjustment = roundMoney(l.priceAdjustment || 0);
        const listPrice = roundMoney(l.listPrice || 0);
        return {
          billingProductId: l.billingProductId ?? null,
          name: l.name.trim(),
          priceAdjustment,
          unitPrice: roundMoney(listPrice + priceAdjustment),
          quantity: l.quantity,
          vatRate: l.vatRate,
        };
      }),
    };

    setSaving(true);
    try {
      const saved = await BillingService.saveCase(payload);
      if (saved.status === 'Draft' || saved.status === 'Cancelled') {
        await BillingService.activateCase(saved.id);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Tahsilat kaydı kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const pageRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    pageRef.current?.scrollIntoView({ block: 'start' });
  }, [open, editing?.id]);

  if (!open) return null;

  return (
    <Box ref={pageRef}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          px: 2,
          py: 1.5,
          mb: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          position: 'sticky',
          top: { xs: 56, md: 64 },
          zIndex: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={onClose} sx={{ textTransform: 'none' }}>
          Listeye dön
        </Button>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {editing ? 'Tahsilat Kaydını Düzenle' : 'Yeni Tahsilat Kaydı'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aynı son tarihteki kayıtlar kalem olarak gelir.
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TRY
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Gönderiliyor...' : 'Gönder'}
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Stack spacing={2} sx={{ flex: '1 1 auto', minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
        <Paper elevation={0} variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Müşteri
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Süresi yaklaşan kayıttan seç. Aynı adlı şirketler şube, cihaz ve seri no ile ayrılır.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
          {!editing && (
            <Autocomplete
              fullWidth
              sx={{ minWidth: 0 }}
              options={reminderOptions}
              loading={loadingRefs}
              getOptionLabel={reminderOptionLabel}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              value={reminderOptions.find((r) => r.key === reminderKey) ?? null}
              onChange={(_, v) => {
                if (!v) {
                  setReminderKey('');
                  return;
                }
                const date = isoDate(v.expiryDate);
                setReminderKey(v.key);
                setExpiryDate(date);
                setSourceType(v.kind === 'FISCAL_DEVICE' ? 'FiscalDevice' : 'Manual');
                setSourceId(v.kind === 'FISCAL_DEVICE' ? sourceIdFromKey(v.key) ?? '' : '');
                if (v.companyId === companyId && (devices.length > 0 || companyProducts.length > 0)) {
                  const mail = companies.find((c) => c.id === companyId)?.admin?.email || '';
                  applyDate(date, devices, companyProducts, branches, mail);
                  return;
                }
                setCompanyId(v.companyId);
                setBranchId('');
                setAnnouncementScope(v.kind === 'FISCAL_DEVICE' ? 'Branches' : 'Company');
                setNotifyEmail(companies.find((c) => c.id === v.companyId)?.admin?.email ?? '');
                setLines([{ ...emptyLine }]);
                setDevices([]);
                setCompanyProducts([]);
                setBranches([]);
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.key}>
                  <Box sx={{ py: 0.25, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {option.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {[option.subtitle, option.detail, trDate(isoDate(option.expiryDate)), reminderDaysLabel(option.daysRemaining)]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Süresi yaklaşan kayıt"
                  placeholder="Şirket, şube veya seri no"
                />
              )}
            />
          )}
          <Autocomplete
            fullWidth
            sx={{ minWidth: 0 }}
            options={sortedCompanies}
            loading={loadingRefs}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={companies.find((c) => c.id === companyId) ?? null}
            onChange={(_, v) => {
              setCompanyId(v?.id ?? '');
              if (!editing) {
                setReminderKey('');
                setBranchId('');
                setSourceType('Manual');
                setSourceId('');
                setExpiryDate('');
                setAnnouncementScope('Company');
                setNotifyEmail(v?.admin?.email ?? '');
                setLines([{ ...emptyLine }]);
                setDevices([]);
                setCompanyProducts([]);
                setBranches([]);
              }
            }}
            disabled={!!editing}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Müşteri şirket"
                required
                placeholder="Şirket ara"
                helperText={loadingRefs ? 'Şirketler yükleniyor...' : undefined}
              />
            )}
          />
            <TextField
              label="Son tarih"
              type="date"
              value={expiryDate}
              onChange={(e) => {
                const next = e.target.value;
                if (!next || editing || !companyId) {
                  setExpiryDate(next);
                  return;
                }
                const mail = companies.find((c) => c.id === companyId)?.admin?.email || '';
                applyDate(next, devices, companyProducts, branches, mail);
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              helperText={
                !companyId
                  ? 'Şirket seçilince dolar'
                  : loadingCompany
                    ? 'Kayıtlar getiriliyor'
                    : activeGroup
                    ? `${trDate(activeGroup.date)} · ${groupSummary(activeGroup)} kalem olarak eklendi`
                    : dateGroups.length === 0
                      ? 'Tarihli kayıt yok. Kalemi elle ekleyebilirsin.'
                      : 'Bu tarihte kayıt yok'
              }
            />
            <TextField
              select
              label="Fatura Şirketi"
              value={issuerCompanyId}
              onChange={(e) => setIssuerCompanyId(e.target.value === '' ? '' : Number(e.target.value))}
              fullWidth
            >
              <MenuItem value="">(Seçilmedi)</MenuItem>
              {issuers.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.title}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        <Paper elevation={0} variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Tahsil edilecek tutar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fark artıysa eklenir, eksiyle düşer. Aynı tarihteki kayıtlar kendiliğinden dolar.
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 44, fontWeight: 700 }}>No</TableCell>
                <TableCell>Tür</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Kalem</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Seri no</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Şube</TableCell>
                <TableCell align="right">Liste fiyat</TableCell>
                <TableCell align="right">Fark (+/−)</TableCell>
                <TableCell align="right">Birim</TableCell>
                <TableCell align="right">Adet</TableCell>
                <TableCell align="right">KDV</TableCell>
                <TableCell align="right">Toplam</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, idx) => {
                const serviceName = products.find((p) => p.id === l.billingProductId)?.name || '';
                return (
                <TableRow key={idx}>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700, width: 44 }}>{idx + 1}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {l.kind === 'device' ? 'Yazarkasa' : l.kind === 'product' ? 'Ürün' : 'Elle'}
                  </TableCell>
                  <TableCell>
                    {l.kind === 'manual' ? (
                      <Autocomplete
                        freeSolo
                        options={products}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
                        value={products.find((p) => p.id === l.billingProductId) ?? null}
                        onChange={(_, v) => {
                          if (typeof v === 'string') {
                            updateLine(idx, { name: v, billingProductId: null });
                          } else {
                            applyProductToLine(idx, v);
                          }
                        }}
                        inputValue={l.name}
                        onInputChange={(_, v, reason) => {
                          if (reason === 'input') updateLine(idx, { name: v, billingProductId: null });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Hizmet adı" size="small" />
                        )}
                        sx={{ minWidth: 180 }}
                      />
                    ) : (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>
                          {l.kind === 'device' ? l.deviceName || l.name : l.name}
                        </Typography>
                        {l.kind === 'device' &&
                          serviceName &&
                          serviceName.toLocaleLowerCase('tr') !== (l.deviceName || '').toLocaleLowerCase('tr') && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {serviceName}
                            </Typography>
                          )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: l.serialNumber ? 600 : 400 }}>
                    {l.serialNumber || '—'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'normal' }}>{l.branchName || '—'}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={l.listPrice}
                      onChange={(e) => updateLinePrice(idx, { listPrice: Number(e.target.value) })}
                      sx={{ width: 110 }}
                      inputProps={{ step: '0.01' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={adjustDraft[idx] !== undefined ? adjustDraft[idx] : String(l.priceAdjustment ?? 0)}
                      onChange={(e) => {
                        const raw = e.target.value.replace(',', '.');
                        setAdjustDraft((d) => ({ ...d, [idx]: raw }));
                        if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
                          updateLinePrice(idx, { priceAdjustment: 0 });
                          return;
                        }
                        const n = Number(raw);
                        if (Number.isFinite(n)) updateLinePrice(idx, { priceAdjustment: n });
                      }}
                      onBlur={() =>
                        setAdjustDraft((d) => {
                          const next = { ...d };
                          delete next[idx];
                          return next;
                        })
                      }
                      placeholder="0"
                      sx={{
                        width: 110,
                        '& input': {
                          fontWeight: 600,
                          color:
                            l.priceAdjustment > 0
                              ? 'success.main'
                              : l.priceAdjustment < 0
                                ? 'error.main'
                                : 'inherit',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {money(l.unitPrice || 0)}
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={l.quantity}
                      onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) })}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={l.vatRate}
                      onChange={(e) => updateLine(idx, { vatRate: Number(e.target.value) })}
                      sx={{ width: 70 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {((l.unitPrice || 0) * (l.quantity || 0)).toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={lines.length === 1}
                      onClick={() => {
                        setAdjustDraft({});
                        setLines((prev) => prev.filter((_, i) => i !== idx));
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
          <Box>
            <Button startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...emptyLine }])}>
              Kalem Ekle
            </Button>
          </Box>
          <Box sx={{ textAlign: 'right', fontWeight: 600, mt: 1 }}>
            Genel Toplam: {total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TRY
          </Box>
        </Paper>
        </Stack>

        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            borderRadius: 2,
            width: { xs: '100%', lg: 320 },
            flex: { lg: '0 0 320px' },
            position: { lg: 'sticky' },
            top: { lg: 136 },
            maxHeight: { xs: 'min(520px, 70vh)', lg: 'calc(100vh - 152px)' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            alignSelf: 'flex-start',
          }}
        >
          <Box sx={{ p: 2, pb: 1.5, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Bilgiler
            </Typography>
            <Stack spacing={1}>
              <InfoLine label="Şube" value={!companyId ? '—' : branchText} />
              <InfoLine label="Franchise" value={franchiseLabel || '—'} />
              <InfoLine label="Kaynak" value={companyId ? sourceLabel : '—'} />
              <InfoLine label="Duyuru" value={companyId ? scopeLabel : '—'} />
            </Stack>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <TextField
                size="small"
                label="E-posta"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Telefon"
                value={notifyPhone}
                onChange={(e) => setNotifyPhone(e.target.value)}
                fullWidth
              />
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Aynı tarih
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Yalnız seçilen tarihteki kayıtlar kalem olur.
            </Typography>
          </Box>

          <Box sx={{ px: 2, pb: 2, overflowY: 'auto', minHeight: 0, flex: '0 1 auto' }}>
          {!companyId && (
            <Typography variant="body2" color="text.secondary">
              Şirket seçilince tarihler burada listelenir.
            </Typography>
          )}
          {companyId && dateGroups.length === 0 && !loadingCompany && (
            <Typography variant="body2" color="text.secondary">
              Tarihli kayıt yok. Kalemi elle ekleyebilirsin.
            </Typography>
          )}
          <Stack spacing={0.75}>
            {dateGroups.map((group) => {
              const selected = group.date === expiryDate;
              return (
                <Box
                  key={group.date}
                  onClick={() => {
                    if (editing || selected) return;
                    const mail = companies.find((c) => c.id === companyId)?.admin?.email || '';
                    applyDate(group.date, devices, companyProducts, branches, mail);
                  }}
                  sx={{
                    px: 1.25,
                    py: 0.9,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'action.selected' : 'transparent',
                    cursor: editing || selected ? 'default' : 'pointer',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {trDate(group.date)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {groupSummary(group)}
                    </Typography>
                  </Box>
                  {selected && (
                    <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                      {group.devices.map((device) => (
                        <Typography key={`d-${device.id}`} variant="caption" color="text.secondary">
                          {shortDevice(device)}
                        </Typography>
                      ))}
                      {group.products.map((product) => (
                        <Typography key={`p-${product.id}`} variant="caption" color="text.secondary">
                          {product.name}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CaseFormDialog;
